'use client'

import { Button, Field, Icon, TemplatePreview } from '@plura/client/components';
import { FilterTypes, TemplateItem, templates } from '@plura/shared/lead-forms';
import { useEffect, useMemo, useRef, useState } from 'react';
import BreadCrumbs from '../../molecules/BreadCrumbs';
import TemplateCard from './TemplateCard';
import { FilterCategory, filterGroups } from './templates';

interface Props {
  onTryAiClick?: () => void;
  onUseTemplate?: (template: TemplateItem) => void;
  isProcessing?: string | null;
  withLinks?: boolean
  recommendedIds?: string[]
}

// Templates ship with a demo brand logo so the preview looks complete. Strip
// it when the template is actually applied so it isn't added to the user's form.
const stripTemplateLogo = (template: TemplateItem): TemplateItem => ({
  ...template,
  inputData: () => ({ ...template.inputData(), logoUrl: null }),
});

// Special category for recommended templates
const RECOMMENDED_CATEGORY: FilterCategory = {
  label: 'Recommended',
  value: 'recommended' as FilterTypes,
  description: 'Templates recommended for you based on your preferences.',
};

const TemplatePicker = ({ onTryAiClick, onUseTemplate, isProcessing, withLinks, recommendedIds }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory | null>(null);
  const [showRecommendedOnly, setShowRecommendedOnly] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const inputData = selectedTemplate?.inputData()

  const handleUseTemplate = (template: TemplateItem) => {
    onUseTemplate?.(stripTemplateLogo(template));
  };

  const selectedDescription = showRecommendedOnly
    ? RECOMMENDED_CATEGORY.description
    : selectedCategory?.description
    ?? 'Browse ready-made templates to spark ideas and jump-start your next form or quiz.'

  const filterTemplates = (category: FilterTypes) => {
    setShowRecommendedOnly(false);
    setSelectedCategory(filterGroups.flatMap(group => group.options).find(option => option.value === category) ?? null);
  }

  const filterRecommended = () => {
    setSelectedCategory(null);
    setShowRecommendedOnly(true);
  }

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Calculate relevance score for search matching
  const calculateRelevance = (template: TemplateItem, search: string): number => {
    if (!search) return 0;

    const searchLower = search.toLowerCase();
    const titleLower = template.title.toLowerCase();
    const descriptionLower = template.description.toLowerCase();

    let score = 0;

    if (titleLower === searchLower) {
      score += 100;
    } else if (titleLower.startsWith(searchLower)) {
      score += 80;
    } else if (titleLower.includes(searchLower)) {
      score += 60;
    }

    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 2);
    searchWords.forEach(word => {
      if (titleLower.includes(word)) {
        score += 30;
      }
      if (descriptionLower.includes(word)) {
        score += 10;
      }
    });

    if (searchWords.length > 1) {
      const pattern = searchWords.join('.*');
      const regex = new RegExp(pattern, 'i');
      if (regex.test(titleLower)) {
        score += 40;
      }
      if (regex.test(descriptionLower)) {
        score += 20;
      }
    }

    return score;
  };

  const isRecommended = (templateId: string): boolean => {
    return recommendedIds?.includes(templateId) ?? false;
  };

  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    if (showRecommendedOnly && recommendedIds && recommendedIds.length > 0) {
      filtered = filtered.filter(t => recommendedIds.includes(t.id));
    } else if (selectedCategory) {
      filtered = filtered.filter(template =>
        template.tags.includes(selectedCategory.value)
      );
    }

    if (debouncedSearchTerm) {
      const templatesWithScores = filtered
        .map(template => ({
          template,
          score: calculateRelevance(template, debouncedSearchTerm)
        }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);

      filtered = templatesWithScores.map(({ template }) => template);
    }

    if (!showRecommendedOnly && recommendedIds && recommendedIds.length > 0) {
      const recommended = filtered.filter(t => recommendedIds.includes(t.id));
      const notRecommended = filtered.filter(t => !recommendedIds.includes(t.id));
      return [...recommended, ...notRecommended];
    }

    return filtered;
  }, [selectedCategory, debouncedSearchTerm, recommendedIds, showRecommendedOnly]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [selectedCategory]);

  if (selectedTemplate) {
    return (
      <div className="flex flex-row gap-6 w-full h-full max-md:flex-col max-md:overflow-y-auto">
        <TemplatePreview
          goBack={() => setSelectedTemplate(null)}
          template={selectedTemplate}
          action={inputData && (
            <Button
              isBlack
              size="medium"
              onClick={() => handleUseTemplate(selectedTemplate)}
              disabled={isProcessing === selectedTemplate?.id}
              processing={isProcessing === selectedTemplate?.id}
            >
              Use This Template
              {isProcessing !== selectedTemplate?.id && (
                <Icon name="arrow" className="fill-current !size-6" />
              )}
            </Button>
          )}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-row gap-6 w-full h-full max-md:flex-col max-md:overflow-y-auto">
      {/* Sidebar */}
      <div className="w-[250px] shrink-0 flex flex-col gap-6 h-full overflow-y-auto pt-15 pb-15 pl-5 max-md:w-full max-md:overflow-y-visible max-md:order-2 max-md:pt-5 max-md:pb-5 max-md:pl-0 max-md:px-5">
        <div className="flex flex-col gap-7.5 items-start w-full">
          {recommendedIds && recommendedIds.length > 0 && (
            <div className="flex flex-col gap-0.5 items-start w-full">
              <h5 className="text-sub-title-1 pl-5 mb-1.5">For You</h5>
              <button
                className={`w-full text-left px-5 h-[42px] rounded-xl text-body-2 cursor-pointer transition-colors hover:bg-b-surface1 dark:hover:bg-b-depth2 ${showRecommendedOnly ? 'bg-b-surface1 dark:bg-b-depth2 font-semibold text-t-primary' : 'font-medium text-t-secondary'}`}
                onClick={filterRecommended}
              >
                Recommended
              </button>
            </div>
          )}
          {filterGroups.map((group) => (
            <div key={group.title} className="flex flex-col gap-0.5 items-start w-full">
              <h5 className="text-sub-title-1 pl-5 mb-1.5">{group.title}</h5>
              {group.options.map((option) => (
                <button
                  key={option.value}
                  className={`w-full text-left px-5 h-[42px] rounded-xl text-body-2 cursor-pointer transition-colors hover:bg-b-surface1 dark:hover:bg-b-depth2 ${!showRecommendedOnly && selectedCategory?.value === option.value ? 'bg-b-surface1 dark:bg-b-depth2 font-semibold text-t-primary' : 'font-medium text-t-secondary'}`}
                  onClick={() => filterTemplates(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div ref={contentRef} className="flex-1 h-full overflow-y-auto pb-15 pt-15 pr-10 max-md:pb-5 max-md:pt-10 max-md:pr-5 max-md:pl-5 max-md:overflow-y-visible max-md:h-auto">
        <div className="flex flex-col gap-2.5">
          {(selectedCategory || showRecommendedOnly) && (
            <BreadCrumbs
              items={[
                {
                  title: 'All Templates',
                  onClick: () => {
                    setSelectedCategory(null);
                    setShowRecommendedOnly(false);
                  },
                },
                {
                  title: showRecommendedOnly
                    ? RECOMMENDED_CATEGORY.label
                    : selectedCategory ? selectedCategory.label : 'All Templates',
                  onClick: () => {
                    if (showRecommendedOnly) {
                      filterRecommended();
                    } else if (selectedCategory) {
                      filterTemplates(selectedCategory.value);
                    }
                  },
                },
              ]}
            />
          )}
          <h4 className="text-h3">
            {
              showRecommendedOnly
                ? RECOMMENDED_CATEGORY.label
                : selectedCategory
                  ? selectedCategory.isTemplate ? `${selectedCategory.label} Templates` : selectedCategory.label
                  : 'All Templates'
            }
          </h4>
          <p className="text-h6 font-medium text-t-secondary">{selectedDescription}</p>

          {/* Search */}
          <div className="relative mt-4">
            <Icon className="absolute left-4.5 top-1/2 -translate-y-1/2 z-1 !size-5 fill-t-tertiary pointer-events-none" name="search" />
            <Field
              classInput="bg-b-surface2 !pl-11"
              placeholder="Search all templates and categories"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* Template grid */}
          {filteredTemplates.length > 0 ? (
            <div className="flex flex-wrap mt-4 -mx-3 max-md:flex-col max-md:gap-3 max-md:m-0 max-md:mt-5">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  className="w-[calc(33.333%-1.5rem)] mt-6 mx-3 max-lg:w-[calc(50%-1.5rem)] max-md:w-full max-md:m-0"
                  key={template.id}
                  template={template}
                  onPreview={() => setSelectedTemplate(template)}
                  onUseTemplate={onUseTemplate ? handleUseTemplate : undefined}
                  isProcessing={isProcessing === template.id}
                  withLinks={withLinks}
                  isRecommended={isRecommended(template.id)}
                />
              ))}
            </div>
          ) : (
            <div className="flex w-full flex-col justify-center gap-5 bg-b-surface1 dark:bg-b-depth2 items-center py-15 px-5 rounded-xl border-2 border-s-stroke2 mt-10">
              <h4 className="text-h4 leading-none">Didn&apos;t find what you&apos;re looking for?</h4>
              <p className="text-body-1 text-t-secondary">Try using our AI form builder and get exactly what you need.</p>
              {onTryAiClick && (
                <Button isBlack onClick={onTryAiClick}>
                  Try with AI
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TemplatePicker
